// frontend/src/utils/api.js
// Centralized API utility that automatically includes auth token with requests

const getToken = () => localStorage.getItem('token');

/**
 * Authenticated fetch wrapper that automatically includes the Authorization header.
 * Use this instead of fetch() for all protected API endpoints.
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
        ...options.headers,
    };

    // Add Authorization header if token exists
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Add Content-Type for JSON if body is present and not FormData
    if (options.body && !(options.body instanceof FormData)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    return fetch(url, {
        ...options,
        headers,
    });
};

/**
 * Authenticated fetch that automatically parses JSON response.
 * Throws error if response is not ok.
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const authFetchJson = async (url, options = {}) => {
    const response = await authFetch(url, options);

    if (!response.ok) {
        // Handle auth errors
        if (response.status === 401) {
            // Token expired or invalid - clear storage and redirect
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/';
            throw new Error('Session expired. Please log in again.');
        }

        if (response.status === 403) {
            throw new Error('Access denied. You do not have permission to access this resource.');
        }

        // Try to get error message from response
        let errorMessage = `Request failed with status ${response.status}`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
        } catch (e) {
            // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
    }

    return response.json();
};

export default authFetch;
